/**
 * Utility functions for exporting parcel comparison data to Excel
 */

import ExcelJS from 'exceljs';

/**
 * Helper function to get capacity value for display
 */
const getCapacityDisplay = (parcel) => {
    if (Array.isArray(parcel.nearby_circuits) && parcel.nearby_circuits.length > 0) {
        const capacities = parcel.nearby_circuits
            .map(circuit => circuit.capacity_kw)
            .filter(cap => cap != null);
        if (capacities.length > 0) {
            return `${Math.max(...capacities)} kW`;
        }
    } else if (parcel.nearby_circuits_ladwp != null && parcel.nearby_circuits_ladwp !== 'N/A') {
        if (Array.isArray(parcel.nearby_circuits_ladwp)) {
            const capacities = parcel.nearby_circuits_ladwp.filter(c => c.capacity_kw);
            if (capacities.length > 0) {
                // Sort and get highest
                capacities.sort((a, b) => {
                    const aMatch = a.capacity_kw?.match(/(\d+)/);
                    const bMatch = b.capacity_kw?.match(/(\d+)/);
                    const aVal = aMatch ? parseInt(aMatch[1]) : -1;
                    const bVal = bMatch ? parseInt(bMatch[1]) : -1;
                    return bVal - aVal;
                });
                return capacities[0].capacity_kw;
            }
        } else if (typeof parcel.nearby_circuits_ladwp === 'object') {
            return parcel.nearby_circuits_ladwp.capacity_kw ?? 'N/A';
        }
    }
    return 'N/A';
};

/**
 * Helper function to get circuit information
 */
const getCircuitInfo = (parcel) => {
    if (Array.isArray(parcel.nearby_circuits) && parcel.nearby_circuits.length > 0) {
        return parcel.nearby_circuits.map(circuit => ({
            name: circuit.circuit_name || 'N/A',
            capacity: circuit.capacity_kw ? `${circuit.capacity_kw} kW` : 'N/A',
            distance: circuit.distance_miles ? `${circuit.distance_miles.toFixed(2)} mi` : 'N/A',
            voltage: circuit.circuit_voltage ? `${circuit.circuit_voltage} kV` : 'N/A',
            substation: circuit.substation_name || 'N/A'
        }));
    } else if (parcel.nearby_circuits_ladwp != null && parcel.nearby_circuits_ladwp !== 'N/A') {
        const circuits = Array.isArray(parcel.nearby_circuits_ladwp) 
            ? parcel.nearby_circuits_ladwp 
            : [parcel.nearby_circuits_ladwp];
        
        return circuits.map(circuit => ({
            name: circuit.circuit_name || circuit.circuit_id || 'N/A',
            capacity: circuit.capacity_kw || 'N/A',
            distance: circuit.distance_miles ? `${circuit.distance_miles.toFixed(2)} mi` : 'N/A',
            voltage: circuit.circuit_voltage ? `${circuit.circuit_voltage} kV` : 'N/A',
            substation: circuit.substation_name || 'N/A'
        }));
    }
    return [];
};

/**
 * Export parcels comparison to Excel with multiple sheets
 */
export const exportParcelsToExcel = async (parcels) => {
    if (!parcels || parcels.length === 0) {
        alert('No parcels to export');
        return;
    }

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'AmpTrans';
    workbook.created = new Date();

    // Sort parcels by recommendation score
    const sortedParcels = [...parcels].sort((a, b) => 
        (b.recommendation_score || 0) - (a.recommendation_score || 0)
    );

    // Sheet 1: Summary of All Parcels
    const summarySheet = workbook.addWorksheet('All Parcels Summary', {
        views: [{ state: 'frozen', xSplit: 0, ySplit: 1 }]
    });

    // Add title
    summarySheet.mergeCells('A1:I1');
    const titleCell = summarySheet.getCell('A1');
    titleCell.value = 'Land Parcel Comparison Report';
    titleCell.font = { size: 16, bold: true, color: { argb: 'FF2980B9' } };
    titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
    summarySheet.getRow(1).height = 30;

    // Add date
    summarySheet.mergeCells('A2:I2');
    const dateCell = summarySheet.getCell('A2');
    dateCell.value = `Generated: ${new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    })}`;
    dateCell.font = { size: 10, italic: true };
    dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
    summarySheet.getRow(2).height = 20;

    // Add note
    summarySheet.mergeCells('A3:I3');
    const noteCell = summarySheet.getCell('A3');
    noteCell.value = 'Parcels are listed in descending order based on our recommendation criteria. See Technical Details for more information.';
    noteCell.font = { size: 9, italic: true };
    noteCell.alignment = { vertical: 'middle', horizontal: 'center' };
    summarySheet.getRow(3).height = 18;

    // Headers
    const headers = ['Rank', 'Address', 'APN', 'ZIP Code', 'Land Value', 'Area (sqft)', 'Zone', 'Utility', 'Capacity'];
    const headerRow = summarySheet.getRow(5);
    headers.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF3498DB' }
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };
    });
    headerRow.height = 25;

    // Data rows
    sortedParcels.forEach((parcel, index) => {
        const row = summarySheet.getRow(6 + index);
        const rowData = [
            index + 1,
            parcel.Address || 'N/A',
            parcel.APN || 'N/A',
            parcel.Zip5 || parcel.Zip || 'N/A',
            parcel.Land_Value ? parseFloat(parcel.Land_Value) : 'N/A',
            parcel.Area ? parseFloat(parcel.Area.toFixed(2)) : 'N/A',
            parcel.zone_cla_1 || 'N/A',
            parcel.utility_region_acronym || 'N/A',
            getCapacityDisplay(parcel)
        ];

        rowData.forEach((value, colIndex) => {
            const cell = row.getCell(colIndex + 1);
            cell.value = value;
            cell.border = {
                top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
            };
            
            // Highlight top 5 parcels
            if (index < 5) {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFE8F5E9' } // Light green
                };
            }

            // Format currency
            if (colIndex === 4 && typeof value === 'number') {
                cell.numFmt = '$#,##0';
            }

            // Format numbers
            if (colIndex === 5 && typeof value === 'number') {
                cell.numFmt = '#,##0.00';
            }
        });
    });

    // Set column widths
    summarySheet.columns = [
        { width: 8 },   // Rank
        { width: 35 },  // Address
        { width: 15 },  // APN
        { width: 12 },  // ZIP
        { width: 15 },  // Land Value
        { width: 12 },  // Area
        { width: 12 },  // Zone
        { width: 15 },  // Utility
        { width: 15 }   // Capacity
    ];

    // Sheet 2: Top 5 Recommended Parcels - Detailed
    const top5Parcels = sortedParcels.slice(0, 5);
    
    top5Parcels.forEach((parcel, index) => {
        const sheetName = `Top ${index + 1} - ${(parcel.Address || 'Parcel').substring(0, 20)}`;
        const detailSheet = workbook.addWorksheet(sheetName);

        let currentRow = 1;

        // Title
        detailSheet.mergeCells(`A${currentRow}:B${currentRow}`);
        const titleCell = detailSheet.getCell(`A${currentRow}`);
        titleCell.value = `Rank #${index + 1} - ${parcel.Address || 'N/A'}`;
        titleCell.font = { size: 14, bold: true, color: { argb: 'FF2980B9' } };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
        detailSheet.getRow(currentRow).height = 25;
        currentRow += 2;

        // Property Details Section
        detailSheet.mergeCells(`A${currentRow}:B${currentRow}`);
        const propertyHeader = detailSheet.getCell(`A${currentRow}`);
        propertyHeader.value = 'PROPERTY DETAILS';
        propertyHeader.font = { size: 12, bold: true, color: { argb: 'FF2980B9' } };
        propertyHeader.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE3F2FD' }
        };
        currentRow++;

        const propertyDetails = [
            ['APN', parcel.APN || 'N/A'],
            ['Address', parcel.Address || 'N/A'],
            ['ZIP Code', parcel.Zip5 || parcel.Zip || 'N/A'],
            ['Land Value', parcel.Land_Value ? `$${parseInt(parcel.Land_Value).toLocaleString()}` : 'N/A'],
            ['Area (sq ft)', parcel.Area ? parcel.Area.toFixed(2) : 'N/A'],
            ['Latitude', parcel.Center_Lat?.toFixed(6) || 'N/A'],
            ['Longitude', parcel.Center_Lon?.toFixed(6) || 'N/A']
        ];

        propertyDetails.forEach(([key, value]) => {
            const row = detailSheet.getRow(currentRow);
            row.getCell(1).value = key;
            row.getCell(1).font = { bold: true };
            row.getCell(2).value = value;
            currentRow++;
        });

        currentRow++; // Space

        // Zoning & Jurisdiction Section
        detailSheet.mergeCells(`A${currentRow}:B${currentRow}`);
        const zoningHeader = detailSheet.getCell(`A${currentRow}`);
        zoningHeader.value = 'ZONING & JURISDICTION';
        zoningHeader.font = { size: 12, bold: true, color: { argb: 'FF2980B9' } };
        zoningHeader.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE3F2FD' }
        };
        currentRow++;

        const zoningDetails = [
            ['Zone Classification', parcel.zone_cla_1 || 'N/A'],
            ['Zone Description', parcel.zone_Descr || 'N/A'],
            ['Zone Code', parcel.zone_Code || 'N/A'],
            ['Jurisdiction', parcel.zone_Juris || 'N/A']
        ];

        zoningDetails.forEach(([key, value]) => {
            const row = detailSheet.getRow(currentRow);
            row.getCell(1).value = key;
            row.getCell(1).font = { bold: true };
            row.getCell(2).value = value;
            currentRow++;
        });

        currentRow++; // Space

        // Utility Information Section
        detailSheet.mergeCells(`A${currentRow}:B${currentRow}`);
        const utilityHeader = detailSheet.getCell(`A${currentRow}`);
        utilityHeader.value = 'UTILITY INFORMATION';
        utilityHeader.font = { size: 12, bold: true, color: { argb: 'FF2980B9' } };
        utilityHeader.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE3F2FD' }
        };
        currentRow++;

        const utilityDetails = [
            ['Utility Service Provider', parcel.utility_region_acronym || 'N/A'],
            ['Max Capacity', getCapacityDisplay(parcel)]
        ];

        utilityDetails.forEach(([key, value]) => {
            const row = detailSheet.getRow(currentRow);
            row.getCell(1).value = key;
            row.getCell(1).font = { bold: true };
            row.getCell(2).value = value;
            currentRow++;
        });

        currentRow += 2; // Space

        // Nearby Circuits Section
        const circuits = getCircuitInfo(parcel);
        if (circuits.length > 0) {
            detailSheet.mergeCells(`A${currentRow}:E${currentRow}`);
            const circuitHeader = detailSheet.getCell(`A${currentRow}`);
            circuitHeader.value = 'NEARBY CIRCUITS';
            circuitHeader.font = { size: 12, bold: true, color: { argb: 'FF2980B9' } };
            circuitHeader.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFE3F2FD' }
            };
            currentRow++;

            // Circuit headers
            const circuitHeaders = ['Circuit Name', 'Capacity', 'Distance', 'Voltage', 'Substation'];
            const circuitHeaderRow = detailSheet.getRow(currentRow);
            circuitHeaders.forEach((header, colIndex) => {
                const cell = circuitHeaderRow.getCell(colIndex + 1);
                cell.value = header;
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FF3498DB' }
                };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            });
            currentRow++;

            // Circuit data
            circuits.forEach(circuit => {
                const row = detailSheet.getRow(currentRow);
                [circuit.name, circuit.capacity, circuit.distance, circuit.voltage, circuit.substation]
                    .forEach((value, colIndex) => {
                        row.getCell(colIndex + 1).value = value;
                    });
                currentRow++;
            });
        }

        // Set column widths
        detailSheet.columns = [
            { width: 25 },
            { width: 50 },
            { width: 15 },
            { width: 15 },
            { width: 25 }
        ];
    });

    // Sheet 3: All Circuits Details
    const circuitsSheet = workbook.addWorksheet('All Circuits Details');

    // Add title
    circuitsSheet.mergeCells('A1:G1');
    const circuitsTitleCell = circuitsSheet.getCell('A1');
    circuitsTitleCell.value = 'Nearby Circuits - All Parcels';
    circuitsTitleCell.font = { size: 14, bold: true, color: { argb: 'FF2980B9' } };
    circuitsTitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
    circuitsSheet.getRow(1).height = 25;

    // Headers
    const circuitHeaders = ['Parcel Address', 'APN', 'Circuit Name', 'Capacity', 'Distance', 'Voltage', 'Substation'];
    const circuitHeaderRow = circuitsSheet.getRow(3);
    circuitHeaders.forEach((header, index) => {
        const cell = circuitHeaderRow.getCell(index + 1);
        cell.value = header;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF3498DB' }
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });
    circuitHeaderRow.height = 25;

    // Data rows
    let circuitRowIndex = 4;
    sortedParcels.forEach(parcel => {
        const circuits = getCircuitInfo(parcel);
        if (circuits.length > 0) {
            circuits.forEach(circuit => {
                const row = circuitsSheet.getRow(circuitRowIndex);
                [
                    parcel.Address || 'N/A',
                    parcel.APN || 'N/A',
                    circuit.name,
                    circuit.capacity,
                    circuit.distance,
                    circuit.voltage,
                    circuit.substation
                ].forEach((value, colIndex) => {
                    row.getCell(colIndex + 1).value = value;
                });
                circuitRowIndex++;
            });
        } else {
            const row = circuitsSheet.getRow(circuitRowIndex);
            row.getCell(1).value = parcel.Address || 'N/A';
            row.getCell(2).value = parcel.APN || 'N/A';
            row.getCell(3).value = 'No circuits available';
            circuitRowIndex++;
        }
    });

    // Set column widths
    circuitsSheet.columns = [
        { width: 35 },  // Address
        { width: 15 },  // APN
        { width: 25 },  // Circuit Name
        { width: 15 },  // Capacity
        { width: 12 },  // Distance
        { width: 12 },  // Voltage
        { width: 25 }   // Substation
    ];

    // Save the file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Parcel_Comparison_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
};

